import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Form, InputGroup } from 'react-bootstrap';
import { Controller } from 'react-hook-form';
import InputMask from 'react-input-mask';
import { IconBook } from '@tabler/icons-react';
import TinyMCEEditor from '../TinyMCEEditor';
import { CONTACT_TYPES, CREDENTIAL_TYPES, URL_VALIDATION, VALIDATION_MESSAGES } from '../../constants/formConstants';

const Step5Instructions = ({ 
  formData, 
  register, 
  setValue, 
  control,
  errors, 
  saving 
}) => {
  const [platformUrlWarning, setPlatformUrlWarning] = useState(false);

  // Auto-populate platform URL with current origin on component mount
  useEffect(() => {
    if (!formData.platform_url || formData.platform_url.trim() === '') {
      const currentOrigin = window.location.origin;
      setValue("platform_url", currentOrigin);
      // Also validate the auto-populated URL
      if (!URL_VALIDATION.validatePlatformUrl(currentOrigin)) {
        setPlatformUrlWarning(true);
      }
    }
  }, [formData.platform_url, setValue]);

  const handlePlatformUrlChange = (url) => {
    setValue("platform_url", url);
    
    if (url && typeof url === 'string' && url.trim() !== "" && !URL_VALIDATION.validatePlatformUrl(url)) {
      setPlatformUrlWarning(true);
    } else {
      setPlatformUrlWarning(false);
    }
  };

  const getContactIcon = (contactType) => {
    const contact = CONTACT_TYPES.find(c => c.value === contactType);
    return contact ? contact.icon : 'la-address-book';
  };

  return (
    <div className="mpf-step-content">
      <div className="mpf-step-header">
        <IconBook size={24} className="text-primary" />
        <h4>Instruções de Uso</h4>
        <p className="text-muted">Informações de acesso, configuração e suporte</p>
      </div>
      
      {/* Platform Access Section */}
      <Card className="mb-4">
        <Card.Header className="bg-light">
          <h6 className="mb-0 fw-bold">
            <i className="las la-link me-2 text-primary" />
            Acesso à Plataforma <span className="text-danger">*</span>
          </h6>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Nome da Plataforma *</Form.Label>
                <Form.Control
                  type="text"
                  {...register("platform_name")}
                  placeholder="Ex: Mercado Agentes, Minha Empresa e etc..."
                  disabled={saving}
                  isInvalid={!!errors.platform_name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.platform_name?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>URL da Plataforma *</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <i className="las la-globe" />
                  </InputGroup.Text>
                  <Form.Control
                    type="url"
                    value={formData.platform_url || ""}
                    onChange={(e) => handlePlatformUrlChange(e.target.value)}
                    placeholder="https://plataforma.exemplo.com"
                    disabled={saving}
                    isInvalid={!!errors.platform_url}
                    className={platformUrlWarning ? "border-warning" : ""}
                  />
                </InputGroup>
                {platformUrlWarning && (
                  <div className="text-warning mt-1">
                    <i className="las la-exclamation-triangle me-1" />
                    {VALIDATION_MESSAGES.HTTPS_WARNING}
                  </div>
                )}
                <Form.Control.Feedback type="invalid">
                  {errors.platform_url?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>Descrição da Plataforma *</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              {...register("platform_description")}
              placeholder="Explique qual plataforma o cliente deve usar para utilizar o agente..."
              disabled={saving}
              isInvalid={!!errors.platform_description}
            />
            <Form.Control.Feedback type="invalid">
              {errors.platform_description?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Card.Body>
      </Card>
      
      {/* Access Credentials Section */}
      <Card className="mb-4">
        <Card.Header className="bg-light">
          <h6 className="mb-0 fw-bold">
            <i className="las la-key me-2 text-warning" />
            Credenciais de Acesso <span className="text-danger">*</span>
          </h6>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Tipo de Credencial *</Form.Label>
                <Form.Select
                  {...register("credential_type")}
                  disabled={saving}
                  isInvalid={!!errors.credential_type}
                >
                  <option value="">Selecione o tipo de credencial</option>
                  {CREDENTIAL_TYPES.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.credential_type?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col lg={12}>
              <TinyMCEEditor
                label="Instruções de Acesso *"
                value={formData.access_instructions || ""}
                onChange={(value) => setValue("access_instructions", value)}
                disabled={saving}
                placeholder="Instruções detalhadas de como o cliente deve acessar a plataforma..."
                height={200}
                error={errors.access_instructions?.message}
              />
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>Informações Adicionais</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              {...register("additional_info")}
              placeholder="Informações extras sobre o acesso..."
              disabled={saving}
            />
          </Form.Group>
        </Card.Body>
      </Card>
      
      {/* Agent Configuration Section */}
      <Card className="mb-4">
        <Card.Header className="bg-light">
          <h6 className="mb-0 fw-bold">
            <i className="las la-cogs me-2 text-success" />
            Configuração do Agente <span className="text-danger">*</span>
          </h6>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col lg={12}>
              <TinyMCEEditor
                label="Passos de Configuração *"
                value={formData.configuration_steps || ""}
                onChange={(value) => setValue("configuration_steps", value)}
                disabled={saving}
                placeholder="Passo a passo para configurar o agente..."
                height={200}
                error={errors.configuration_steps?.message}
              />
            </Col>
          </Row>
          <Row>
            <Col lg={12}>
              <TinyMCEEditor
                label="Opções de Personalização *"
                value={formData.customization_options || ""}
                onChange={(value) => setValue("customization_options", value)}
                disabled={saving}
                placeholder="Como o cliente pode personalizar o agente..."
                height={200}
                error={errors.customization_options?.message}
              />
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>Requisitos de Setup *</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              {...register("setup_requirements")}
              placeholder="Requisitos necessários para o setup do agente..."
              disabled={saving}
              isInvalid={!!errors.setup_requirements}
            />
            <Form.Control.Feedback type="invalid">
              {errors.setup_requirements?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Card.Body>
      </Card>
      
      {/* Support Contact Section */}
      <Card className="mb-4">
        <Card.Header className="bg-light">
          <h6 className="mb-0 fw-bold">
            <i className="las la-headset me-2 text-purple" />
            Contato para Suporte <span className="text-danger">*</span>
          </h6>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Tipo de Contato *</Form.Label>
                <Form.Select
                  {...register("contact_type")}
                  disabled={saving}
                  isInvalid={!!errors.contact_type}
                >
                  <option value="">Selecione o tipo de contato</option>
                  {CONTACT_TYPES.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.contact_type?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Contato *</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <i className={`las ${getContactIcon(formData.contact_type)}`} />
                  </InputGroup.Text>
                  {['phone', 'whatsapp'].includes(formData.contact_type) ? (
                    <Controller
                      name="contact_value"
                      control={control}
                      render={({ field }) => (
                        <InputMask
                          mask="(99) 99999-9999"
                          value={field.value || ""}
                          onChange={field.onChange}
                          disabled={saving}
                          className={`form-control ${errors.contact_value ? 'is-invalid' : ''}`}
                          placeholder="(11) 99999-9999"
                        />
                      )}
                    />
                  ) : (
                    <Form.Control
                      type={formData.contact_type === 'email' ? 'email' : 'text'}
                      {...register("contact_value")}
                      placeholder={
                        formData.contact_type === 'email' ? 'suporte@exemplo.com' :
                        formData.contact_type === 'telegram' ? '@usuario' :
                        formData.contact_type === 'discord' ? 'usuario#1234' :
                        'Contato para suporte'
                      }
                      disabled={saving}
                      isInvalid={!!errors.contact_value}
                    />
                  )}
                </InputGroup>
                <Form.Control.Feedback type="invalid">
                  {errors.contact_value?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Nome do Responsável *</Form.Label>
                <Form.Control
                  type="text"
                  {...register("contact_name")}
                  placeholder="Nome da pessoa responsável pelo suporte"
                  disabled={saving}
                  isInvalid={!!errors.contact_name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.contact_name?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Horário de Atendimento *</Form.Label>
                <Form.Control
                  type="text"
                  {...register("support_hours")}
                  placeholder="Ex: Segunda a Sexta, 9h às 18h"
                  disabled={saving}
                  isInvalid={!!errors.support_hours}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.support_hours?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
};

export default Step5Instructions;